import React from 'react';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {prism} from 'react-syntax-highlighter/dist/esm/styles/prism';

const RetrieveRelatedClusteredCategories = ({ onSectionChange }) => {
    return (
        <div className="api-reference-layout">
            <div className="api-content">
                <div className="api-header">
                    <div className="api-method-badge post">POST</div>
                    <h1>retrieve_related_clustered_categories - Retrieve Related Clustered Categories</h1>
                    <p className="api-description">
                        Retrieve related clustered categories based on category query
                    </p>
                </div>

                <div className="api-endpoint">
                    <div className="endpoint-url">
                        <span className="method post">POST</span>
                        <span className="url">/api/v2/memory/retrieve/related-clustered-categories</span>
                    </div>
                </div>

                <div className="api-section">
                    <h2 id="authorization">Authorization</h2>
                    <div className="auth-item">
                        <div className="auth-header">
                            <span className="auth-name">Authorization</span>
                            <span className="auth-type">string</span>
                            <span className="auth-location">header</span>
                            <span className="required-badge">required</span>
                        </div>
                        <p>Bearer authentication header of the form Bearer &lt;token&gt;, where &lt;token&gt; is your API key.</p>
                    </div>
                </div>

                <div className="api-section">
                    <h2 id="request-body">Request Body</h2>
                    <div className="params-container">
                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">user_id</span>
                                <span className="param-type">string</span>
                                <span className="required-badge">required</span>
                            </div>
                            <p>Unique identifier for the user</p>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>"user001"</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">agent_id</span>
                                <span className="param-type">string</span>
                                <span className="optional-badge">optional</span>
                            </div>
                            <p>Agent identifier, if null searches across all agents</p>
                            <div className="param-default">
                                <strong>Default:</strong>
                                <code>null</code>
                            </div>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>"gpt001"</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">category_query</span>
                                <span className="param-type">string</span>
                                <span className="required-badge">required</span>
                            </div>
                            <p>Category search query statement</p>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>"entertainment preferences"</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">top_k</span>
                                <span className="param-type">integer</span>
                                <span className="optional-badge">optional</span>
                            </div>
                            <p>Maximum number of categories to return</p>
                            <div className="param-default">
                                <strong>Default:</strong>
                                <code>5</code>
                            </div>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>3</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">min_similarity</span>
                                <span className="param-type">float</span>
                                <span className="optional-badge">optional</span>
                            </div>
                            <p>Minimum similarity threshold (0.0-1.0)</p>
                            <div className="param-default">
                                <strong>Default:</strong>
                                <code>0.3</code>
                            </div>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>0.5</code>
                            </div>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">want_summary</span>
                                <span className="param-type">boolean</span>
                                <span className="optional-badge">optional</span>
                            </div>
                            <p>Whether to return summaries instead of raw memory items</p>
                            <div className="param-default">
                                <strong>Default:</strong>
                                <code>true</code>
                            </div>
                            <div className="param-example">
                                <strong>Example:</strong>
                                <code>false</code>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="api-section">
                    <h2 id="response">Response</h2>
                    <div className="response-status">
                        <span className="status-code success">200</span>
                        <span className="content-type">application/json</span>
                    </div>

                    <div className="params-container">
                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">clustered_categories</span>
                                <span className="param-type">array[ClusteredCategory]</span>
                            </div>
                            <p>List of clustered categories</p>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">category_query</span>
                                <span className="param-type">string</span>
                            </div>
                            <p>Original category query statement</p>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">total_categories_found</span>
                                <span className="param-type">integer</span>
                            </div>
                            <p>Total number of categories found</p>
                        </div>

                        <div className="param-divider"></div>

                        <div className="param-item">
                            <div className="param-header">
                                <span className="param-name">search_params</span>
                                <span className="param-type">object</span>
                            </div>
                            <p>Search parameters used</p>
                        </div>
                    </div>

                    <div className="response-schema">
                        <h3>ClusteredCategory Structure:</h3>
                        <div className="params-container">
                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">name</span>
                                    <span className="param-type">string</span>
                                </div>
                                <p>Category name</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">user_id</span>
                                    <span className="param-type">string</span>
                                </div>
                                <p>User ID</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">agent_id</span>
                                    <span className="param-type">string</span>
                                </div>
                                <p>Agent ID</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">similarity_score</span>
                                    <span className="param-type">float</span>
                                </div>
                                <p>Similarity score (0.0-1.0)</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">memories</span>
                                    <span className="param-type">array[MemoryItem]</span>
                                </div>
                                <p>List of memory items</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">memory_count</span>
                                    <span className="param-type">integer</span>
                                </div>
                                <p>Number of memories in this category</p>
                            </div>

                            <div className="param-divider"></div>

                            <div className="param-item">
                                <div className="param-header">
                                    <span className="param-name">summary</span>
                                    <span className="param-type">string</span>
                                </div>
                                <p>Category summary</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="api-demo-sidebar">
                <div className="demo-sticky">
                    <div className="demo-header">
                        <div className="demo-tabs">
                            <button className="demo-tab active">Python</button>
                            <button className="demo-tab">JavaScript</button>
                            <button className="demo-tab">cURL</button>
                        </div>
                    </div>

                    <div className="demo-content">
                        <h3 id="examples">Request Example</h3>
                        <SyntaxHighlighter
                            language="python"
                            style={prism}
                            customStyle={{
                                margin: 0,
                                padding: '20px',
                                background: 'white',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '0 0 8px 8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            {`import os
from memu import MemuClient

# Initialize MemU client
memu_client = MemuClient(
    base_url="https://api.memu.so",
    api_key=os.getenv("MEMU_API_KEY")
)

# Search for related clustered categories with summaries
response = memu_client.retrieve_related_clustered_categories(
    user_id="user001",
    agent_id="gpt001",
    category_query="entertainment preferences",
    top_k=3,
    min_similarity=0.5,
    want_summary=True
)

print(f"Found {response.total_categories_found} related categories")
for category in response.clustered_categories:
    print(f"Category: {category.name}")
    print(f"Similarity: {category.similarity_score:.2f}")
    print(f"Memory Count: {category.memory_count}")
    print(f"Summary: {category.summary}")
    print("---")

# Search with detailed memory items
detailed_response = memu_client.retrieve_related_clustered_categories(
    user_id="user001",
    category_query="hobbies and interests",
    want_summary=False,
    top_k=2
)

for category in detailed_response.clustered_categories:
    print(f"Category: {category.name}")
    print(f"Memories:")
    for memory in category.memories:
        print(f"  - {memory.content}")
    print("---")`}
                        </SyntaxHighlighter>
                    </div>

                    <div className="response-section">
                        <div className="response-header">
                            <span className="response-status-code">200</span>
                            <span>Response (Summary Mode)</span>
                        </div>
                        <SyntaxHighlighter
                            language="json"
                            style={prism}
                            customStyle={{
                                margin: 0,
                                padding: '20px',
                                background: 'white',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '0 0 8px 8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            {`{
  "clustered_categories": [
    {
      "name": "Movie Preferences",
      "user_id": "user001",
      "agent_id": "gpt001",
      "similarity_score": 0.89,
      "memories": [],
      "memory_count": 5,
      "summary": "User prefers sci-fi and thriller movies, especially those with complex plots and strong character development"
    },
    {
      "name": "Music Tastes",
      "user_id": "user001",
      "agent_id": "gpt001",
      "similarity_score": 0.76,
      "memories": [],
      "memory_count": 3,
      "summary": "Enjoys classical music and jazz, particularly piano compositions and live performances"
    }
  ],
  "category_query": "entertainment preferences",
  "total_categories_found": 2,
  "search_params": {
    "top_k": 3,
    "min_similarity": 0.5,
    "want_summary": true
  }
}`}
                        </SyntaxHighlighter>
                    </div>

                    <div className="response-section">
                        <div className="response-header">
                            <span className="response-status-code">200</span>
                            <span>Response (Detailed Mode)</span>
                        </div>
                        <SyntaxHighlighter
                            language="json"
                            style={prism}
                            customStyle={{
                                margin: 0,
                                padding: '20px',
                                background: 'white',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '0 0 8px 8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            {`{
  "clustered_categories": [
    {
      "name": "Outdoor Activities",
      "user_id": "user001",
      "agent_id": "gpt001",
      "similarity_score": 0.92,
      "memories": [
        {
          "memory_id": "mem_abc123",
          "category": "hobbies",
          "content": "User loves hiking in the Rocky Mountains every summer",
          "happened_at": "2024-01-15T10:30:00Z",
          "created_at": "2024-01-15T10:35:00Z",
          "updated_at": "2024-01-15T10:35:00Z"
        },
        {
          "memory_id": "mem_def456",
          "category": "hobbies",
          "content": "Enjoys rock climbing and bouldering on weekends",
          "happened_at": "2024-01-10T14:20:00Z",
          "created_at": "2024-01-10T14:25:00Z",
          "updated_at": "2024-01-10T14:25:00Z"
        }
      ],
      "memory_count": 2,
      "summary": ""
    }
  ],
  "category_query": "hobbies and interests",
  "total_categories_found": 1,
  "search_params": {
    "top_k": 2,
    "min_similarity": 0.3,
    "want_summary": false
  }
}`}
                        </SyntaxHighlighter>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RetrieveRelatedClusteredCategories;

